import '../../../assets/css/NewRFM.css';
import '../../../App.css';
import { createIncident, getLocationData } from '../../../utils/AWSAPI';
import { useEffect, useState, useRef, useCallback } from 'react';
import LoadingSpinner from '../../../utils/LoadingSpinner';
import { serializeFile, submitAttachments } from '../../../utils/Utils';
import { useTranslation } from 'react-i18next';
import i18n from "../../Translations/i18n/i18n";  //import i18next
import AttachmentPicker from '../../AttachmentsPicker';


function NewRFM() {
  const { t } = useTranslation();

  // DATA API
  const [isLoading, setLoading] = useState(true);
  const [isSubmitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);

  // Are we showing a modal? I.E: Error, success.
  const [isModalShowing, setModalShowing] = useState(false);
  const [isModalUncloseable, setModalUncloseable] = useState(false);

  const [modalHeader, setModalHeader] = useState(t('loading'));
  const [modalBody, setModalBody] = useState(<></>);
  const [modalFooter, setModalFooter] = useState(<></>);

  //filesState
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [attachmentsBusy, setAttachmentsBusy] = useState(false);
  const [attachmentsValid, setAttachmentsValid] = useState(true);

  const closeModal = () => {
    setModalShowing(false);
  };

  const Modal = () => {
    return (
      <form>
        <div className="modal show">
          <div className="modal-content">
            <div className="modal-header">
              <h4>{modalHeader}</h4>
              {!isModalUncloseable && (
                <span className="close" onClick={closeModal}>&times;</span>
              )}
            </div>
            <div className="modal-body">{modalBody}</div>
            <div className="modal-footer">{modalFooter}</div>
          </div>
        </div>
      </form>
    );
  };

  const updateModal = (header, body, footer) => {
    setModalShowing(!isModalShowing);
    setModalHeader(header);
    setModalBody(body);
    setModalFooter(footer);
  }

  const setDefaultAttributes = async () => {
    const url = new URL(window.location.href);
    const locationValue = url.searchParams.get('location');

    if (!locationValue) {
      updateModal(t('whoops'), t('pageAccessError'))
      return { success: false };
    } else {
      return { success: true, location: locationValue };
    }
  }


  useEffect(() => {
    const loadLanguage = () => {
      const language = localStorage.getItem("language");
      if (language) {
        i18n.changeLanguage(language);
      }
    };
    loadLanguage();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { success, location: fetchedLocation, locationName: fetchedName } = await setDefaultAttributes();
        if (success) {
          const locationData = await getLocationData(fetchedLocation);

          if (locationData.Error) {
            return updateModal(t('error_modal_header'), locationData.Error)
          } else {
            setData(locationData);
            setLoading(false);
          }
        } else {
          console.log("Invalid location. Not calling API.");
        }
      } catch (err) {
        console.log(err);
      }
    };
    if (isLoading) {
      fetchData();
    }
  }, [isLoading]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const handleSubmit = (event) => {
      event.preventDefault();

      if (attachmentsBusy) {
        updateModal("Error", "Please wait until all attachments finish validating.");
        return;
      }

      if (!attachmentsValid) {
        updateModal("Error", "One or more attachments are invalid. Please remove/replace the failed file(s).");
        return;
      }

      setSubmitting(true);
      submitRFMPackage(event);
    };

    setSubmitting(true);
    submitRFMPackage(event);
  }

  const submitRFMPackage = async (event) => {
    try {
      if (data.CallerEmail == null) {
        updateModal("Error", "This restaurant doesn't seem to be entirely setup. Please ask your administrator for assistance.");
        return;
      }

      setSubmitting(true);
      const incident = await createIncident(
        "a8f5fd7a878a961014e652473cbb35c9",
        "pos_sesame",
        "rfm",
        data.CallerEmail,
        data.Name,
        document.getElementById('nname').value || "Unspecified",
        "4",
        "4",
        "Self Service RFM Package Export Request",
        document.getElementById("info").value
      );

      if (incident.Error) {
        console.log(Error);
        return;
      }

      const sysId = incident.SysID;
      const caseNumber = incident.Result;

      const okFiles = attachmentFiles || [];

      if (okFiles.length === 0) {
        console.log("RFM Package submitted without any files attached to it.");

        setModalUncloseable(true);
        updateModal(
          "Success",
          "RFM Package created successfully. Incident Number: " + caseNumber + ". \n\nPlease wait..."
        );
        setSubmitting(false);

        setTimeout(() => {
          window.location.href = `incident?ticketNumber=${caseNumber}`;
        }, 1000);

        return;
      }

      console.log("RFM Package submitted. Uploading attachments one by one...");

      const uploadResults = [];

      for (const file of okFiles) {
        try {
          const serialized = await serializeFile(file);
          const res = await submitAttachments(sysId, serialized ? [serialized] : []);

          uploadResults.push({
            name: file.name,
            ok: !res?.Error,
            details: res?.Detail || res?.Message || res?.Error || ""
          });
        } catch (e) {
          uploadResults.push({
            name: file.name,
            ok: false,
            details: e?.message || "Upload failed."
          });
        }
      }

      const failedUploads = uploadResults.filter(r => !r.ok);

      setModalUncloseable(true);

      let modalText =
        "RFM Package created successfully. Incident Number: " +
        caseNumber +
        ".\n\n";

      if (failedUploads.length > 0) {
        modalText += "Attachments:\n";
        modalText += uploadResults
          .map(r => (r.ok ? ` ${r.name}: uploaded` : ` ${r.name}: failed (${r.details || "Unknown error"})`))
          .join("\n");
        modalText += "\n\nPlease wait...";
      } else {
        modalText += "Attachments uploaded successfully.\n\nPlease wait...";
      }

      updateModal("Success", modalText);

      setSubmitting(false);

      const redirectDelayMs = failedUploads.length > 0 ? 10000 : 1000;

      setTimeout(() => {
        window.location.href = `incident?ticketNumber=${caseNumber}`;
      }, redirectDelayMs);

    } catch (err) {
      console.log(err);
    }
  }


  const handleFilesChange = useCallback((files, rows) => {
    setAttachmentFiles(files || []);

    const busy = (rows || []).some(
      (r) => r.status === "converting" || r.status === "loading"
    );

    const valid = (rows || []).every((r) => r.status === "success");

    setAttachmentsBusy(busy);
    setAttachmentsValid(rows?.length ? valid : true);
  }, []);

  return (
    <div className="App">
      <LoadingSpinner show={isLoading || isSubmitting} />

      {isModalShowing && (
        <Modal />
      )}

      {data && (
        <form onSubmit={handleSubmit}>
          <div className="new-rfm-container">
            <div className="recuadro">
              <h1>
                {t('createRFMPackage')}
              </h1>
              <hr />
              <br />
              <b>
                {t('pleaseNote')}
                <br /><br />
                {t('criticalIssues')}
                <br /><br />
                {t('packageRequest')}
                <br /><br />
                {t('remember')}
              </b>
              <br /><br />
            </div>

            <div className="recuadro" >
              <label htmlFor="fname" className="required">{t('restaurant')}:</label>
              <input type="text" id="fname" name="fname" disabled placeholder={data.Name} value={data.Name} required />
              <br /><br />
              <label htmlFor="lname" className="required">{t('storePhoneNumber')}:</label>
              <input type="text" id="lname" name="lname" disabled placeholder={data.Phone} value={data.Phone} required />
              <br /><br />
              <label htmlFor="nname">{t('preferredCallBack')}:</label>
              <input type="text" id="nname" name="nname" />
              <br /><br />
              <div className="recuadroextra">
                <div className="gris">
                  <h3>
                    {t('provideDetailedDescription')}
                  </h3>
                  <div className="izquierdoa">
                    <p>
                      {t('rfmPackageWillApply')}
                    </p>
                    <p>
                      {t('confirmProgramming')}
                    </p>
                    <p>
                      {t('programmingChangesCompleted')}
                    </p>
                    <p>
                      {t('trackingPurpose')}
                    </p>
                  </div>
                </div>
              </div>

              <br />
              <label htmlFor="info" className="required">{t('issueDescription')}</label>
              <textarea id="info" name="info" rows={10} required></textarea>

              <br></br><br></br>

              <label htmlFor="attachments">{t('addAttachmentsLabel')}</label>
              <AttachmentPicker
                // maxFiles={10}          // optional
                validateInSnow={true}
                accept=".png, .pdf, .heic"
                disabled={isSubmitting}
                onFilesChange={handleFilesChange}
              />

              <br /><br />

              <hr />
              <button
                disabled={isSubmitting || attachmentsBusy || !attachmentsValid}
                className="red-button"
                type="submit"
              ><b>{t('submit')}</b></button>
            </div>
          </div>
        </form>
      )}
    </div>
  )
}

export default NewRFM;